import React, { memo } from "react";
import ReleaseCard from "./ReleaseCard";
import { Label } from "../types";
import { useReleases } from "../context/ReleasesContext";

interface LabelProps {
  label: Label;
}

// Skeleton UI for releases when they're loading
const ReleaseCardSkeleton: React.FC = () => (
  <div className="w-36 sm:w-44 flex-shrink-0 animate-pulse">
    <div className="relative">
      <div className="aspect-square bg-gray-200 rounded-lg"></div>
    </div>
    <div className="px-1 mt-2 sm:mt-3">
      <div className="h-3 sm:h-4 bg-gray-200 rounded mb-2"></div>
      <div className="h-2 sm:h-3 bg-gray-200 rounded mb-2 w-3/4"></div>
      <div className="h-2 sm:h-3 bg-gray-200 rounded mb-2 w-1/2"></div>
      <div className="flex flex-wrap gap-0.5 sm:gap-1">
        <div className="h-4 bg-gray-200 rounded-full w-12"></div>
        <div className="h-4 bg-gray-200 rounded-full w-10"></div>
      </div>
    </div>
  </div>
);

const LabelCard: React.FC<LabelProps> = memo(({ label }) => {
  const { getSessionToggle } = useReleases();

  // Improved loading state detection:
  // Only show loading state when explicitly set to loading OR when releases array is completely empty (not just length 0)
  const isLoading = label.isLoading === true || label.releases === undefined;

  // Check if all displayed releases are checked
  // Uses session toggle if available, otherwise falls back to server-provided listened status
  const allReleasesChecked = !isLoading && label.releases && label.releases.every(release =>
    release.id && (getSessionToggle(release.id) ?? release.listened ?? false)
  );

  return (
    <div className={`rounded-lg p-4 sm:p-6 shadow-lg transition-all duration-300 relative ${
      allReleasesChecked 
        ? 'bg-orange-200/20' 
        : 'bg-orange-300'
    }`}>
      {allReleasesChecked && (
        <div className="absolute top-3 right-3 sm:top-4 sm:right-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 sm:h-7 sm:w-7 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
      )}
      <h2 className="text-xl sm:text-2xl font-semibold mb-4">
        <a href={label.label_url} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600">
          {label.label_name}
        </a>
      </h2>
      
      {/* Scrollable container for releases */}
      <div className="relative">
        <div className="overflow-x-auto pb-4 scrollbar-hide">
          <div className="flex gap-3 sm:gap-4 w-max transition-opacity duration-300">
            {isLoading ? (
              // Show skeleton UI when loading
              Array(5).fill(0).map((_, index) => (
                <ReleaseCardSkeleton key={`skeleton-${label.id}-${index}`} />
              ))
            ) : label.releases && label.releases.length === 0 ? (
              // Show message when no releases are available
              <div className="w-full text-center py-4">
                <p className="text-gray-700">No releases available</p>
              </div>
            ) : (
              // Show actual releases when loaded
              label.releases && label.releases.map((release) => (
                <ReleaseCard
                  key={release.release_url}
                  release={release}
                  labelId={label.id || 0}
                  labelName={label.label_name}
                />
              ))
            )}
          </div>
        </div>
        
        {/* Scroll indicators */}
        <div className="absolute left-0 right-0 bottom-0 h-1 bg-gradient-to-r from-orange-400/50 via-transparent to-orange-400/50 pointer-events-none md:hidden" />
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  if (prevProps.label.id !== nextProps.label.id) return false;
  if (prevProps.label.releases?.length !== nextProps.label.releases?.length) return false;
  if (prevProps.label.isLoading !== nextProps.label.isLoading) return false;

  // Check for changes in 'listened' status of releases
  const prevReleases = prevProps.label.releases || [];
  const nextReleases = nextProps.label.releases || [];
  
  // If the 'listened' property has changed for any release, re-render
  for (let i = 0; i < prevReleases.length; i++) {
    if (prevReleases[i].listened !== nextReleases[i].listened) {
      return false;
    }
  }
  
  return true;
});

export default LabelCard;
